import { z } from 'zod';
import type { DataType } from '@/shared/hooks';
import type { DataStatus } from '@/shared/lib';
import type {
  ColumnDataFormatType,
  ColumnDataType,
} from '@/shared/types';

export enum StringFilterType {
  TEXT = 'text',
  MEDIA = 'media',
}

export type FilterType = DataType | StringFilterType;

export interface FilterConfig {
  id: string;
  name: string;
  group: string;
  type: FilterType;
  dataType: ColumnDataFormatType;
  order: number;
}

const numberFilterValueValidator = z
  .object({
    type: z.literal('closed'),
    value: z.tuple([z.number(), z.number()]),
  })
  .or(
    z.object({
      type: z.literal('openPositive').or(z.literal('openNegative')),
      value: z.number(),
    }),
  );

export type NumberFilterValue = z.infer<typeof numberFilterValueValidator>;

export enum StringFilterSelectionType {
  INCLUDE = 'include',
  EXCLUDE = 'exclude',
  CONTAINS = 'contains',
  NOT_CONTAIN = 'not contain',
}

enum ContainsOperator {
  AND = 'and',
  OR = 'or',
}

const containsValueValidator = z.object({
  type: z
    .literal(StringFilterSelectionType.CONTAINS)
    .or(z.literal(StringFilterSelectionType.NOT_CONTAIN)),
  matchingStrings: z.array(z.string()),
  operator: z.nativeEnum(ContainsOperator),
});

const stringFilterValueValidator = z
  .object({
    type: z
      .literal(StringFilterSelectionType.INCLUDE)
      .or(z.literal(StringFilterSelectionType.EXCLUDE)),
    options: z.array(z.string()),
  })
  .or(containsValueValidator);

export type ContainsValue = z.infer<typeof containsValueValidator>;

export type StringFilterValue = z.infer<typeof stringFilterValueValidator>;

const mediaFilterValueValidator = z
  .object({
    type: z.literal('include').or(z.literal('exclude')),
    options: z.array(z.string()),
  })
  .or(containsValueValidator);

export type MediaFilterValue = z.infer<typeof mediaFilterValueValidator>;

const filterValueValidator = stringFilterValueValidator
  .or(numberFilterValueValidator)
  .or(mediaFilterValueValidator);

export type FilterValue = z.infer<typeof filterValueValidator>;

export const filtersValidator = z.array(
  z.object({
    id: z.string(),
    value: filterValueValidator,
  }),
);

export type Filters = z.infer<typeof filtersValidator>;

export interface StringFilterParams {
  options: string[];
}

export interface MediaFilterParams {
  options: string[];
}

export interface NumberFilterParams {
  min: number;
  max: number;
}

export type FilterParams =
  | StringFilterParams
  | NumberFilterParams
  | MediaFilterParams;

export type FiltersParamsData = Record<
  string,
  {
    status: DataStatus;
    params?: FilterParams;
    type?: FilterType;
    error?: string;
  }
>;

export interface MetricFilter {
  id: string;
  value: NumberFilterValue;
}

export interface DimensionFilter {
  id: string;
  value: StringFilterValue;
}

interface ColumnConfig {
  group: string;
  name: string;
  type: ColumnDataType;
  dataType: ColumnDataFormatType;
  order: number;
}

export type ColumnsConfig = Record<string, ColumnConfig>;
